import { type CurrencyOption } from '@shared/config';
import { formatLocalDate } from '@shared/lib/date';
import { EMPTY_PLACEHOLDER, formatNumber, formatPercent, isEmptyNumber } from '@shared/lib/format';

import { AMOUNT_UNIT_TEXT } from '../config/portfolio.text';
import { type InvestStatus } from '../model/cross-types';

const AMOUNT_DIVISOR: Record<CurrencyOption, number> = { KRW: 1_000_000, USD: 1_000 };

/** 금액: 단위로 나눠 반올림한 정수(천 단위 쉼표) + 단위. KRW 500000000 → "500 백만원", USD 100000 → "100 K$" */
export function formatPortfolioAmount(value: number | null | undefined, unit: CurrencyOption) {
  if (isEmptyNumber(value)) return EMPTY_PLACEHOLDER;
  return `${formatNumber(Math.round(value / AMOUNT_DIVISOR[unit]))}${AMOUNT_UNIT_TEXT[unit]}`;
}

/** 달력 날짜 → "YYYY.MM.DD". 없으면 빈 글자 */
export function formatPortfolioDate(value: string | null | undefined) {
  return value ? formatLocalDate(value) : '';
}

/** IRR(비율 0.184) → "18.4%". 없거나 0.01 미만이면 "-" */
export function formatPortfolioIrr(value: number | null | undefined) {
  if (isEmptyNumber(value) || value < 0.01) return EMPTY_PLACEHOLDER;
  return formatPercent(value, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/**
 * Multiple → "2.5 X". 정확히 정수면 "1 X", 아니면 소수 한 자리를 늘 쓴다(1.02 → "1.0 X") — 기존 FormatIndicator.Multiple.
 * 없거나 1 미만이면 "-"
 */
export function formatPortfolioMultiple(value: number | null | undefined) {
  if (isEmptyNumber(value) || value < 1) return EMPTY_PLACEHOLDER;
  const digits = Number.isInteger(value) ? 0 : 1;
  return `${formatNumber(value, { minimumFractionDigits: digits, maximumFractionDigits: digits })} X`;
}

/** 같은 회사의 상태 우선순위: 폐업 > 폐업예정 > 모두 회수 > 부분회수 > 감액 */
const STATUS_PRIORITY: readonly InvestStatus[] = [
  'Shutdown',
  'PreShutdown',
  'CompleteExit',
  'PartialExit',
  'CompleteImpairment',
  'PartialImpairment',
];

/** 같은 회사 줄들의 status 를 하나로 합친다(전체 목록의 회사명 옆 배지) */
export function mergeInvestStatus(statuses: readonly InvestStatus[]): InvestStatus {
  return STATUS_PRIORITY.find((status) => statuses.includes(status)) ?? 'None';
}

/** 검색어 앞뒤로 남기는 글자 수 */
const SEARCH_CLIP_LENGTH = 60;

export interface ClippedSearchText {
  before: string;
  match: string;
  after: string;
}

/**
 * 본문에서 검색어가 걸린 곳을 앞뒤 60자로 자른다. 걸린 곳이 없으면 null.
 * 앞뒤 말줄임(...)은 그리는 쪽이 붙인다(Figma 는 조각 앞뒤에 늘 붙인다).
 */
export function clipSearchText(content: string, searchText: string): ClippedSearchText | null {
  if (!searchText) return null;
  const index = content.toLowerCase().indexOf(searchText.toLowerCase());
  if (index === -1) return null;
  const end = index + searchText.length;
  return {
    before: content.slice(Math.max(0, index - SEARCH_CLIP_LENGTH), index),
    match: content.slice(index, end),
    after: content.slice(end, end + SEARCH_CLIP_LENGTH),
  };
}
